import { useState, useRef, useCallback, useEffect } from 'react'
import { loadClampedNumber, saveToLocalStorage } from '@/lib/storage/local-storage'

export const MIN_WIDTH = 220
export const MAX_WIDTH = 9999 // Effectively unlimited, actual max is 50% of viewport
const DEFAULT_WIDTH = 220

export const MIN_SECTION_HEIGHT = 60
export const MAX_SECTION_HEIGHT = 400
const DEFAULT_DRAFTS_HEIGHT = 150

/**
 * Manages sidebar width + drafts section height resize with localStorage persistence.
 * Handles global mouse listeners for both horizontal (col-resize) and vertical (row-resize) dragging.
 */
export function useSidebarResize() {
  // Sidebar width state with localStorage persistence
  const [sidebarWidth, setSidebarWidth] = useState(() =>
    loadClampedNumber('myrkup-sidebar-width', DEFAULT_WIDTH, MIN_WIDTH, MAX_WIDTH),
  )
  const [isResizing, setIsResizing] = useState(false)
  const isDragging = useRef(false)
  const dragStartX = useRef(0)
  const dragStartWidth = useRef(0)
  const sidebarRef = useRef<HTMLDivElement>(null)

  // Drafts section height state with localStorage persistence
  const [draftsHeight, setDraftsHeight] = useState(() =>
    loadClampedNumber('myrkup-drafts-height', DEFAULT_DRAFTS_HEIGHT, MIN_SECTION_HEIGHT, MAX_SECTION_HEIGHT),
  )
  const isDraggingSection = useRef(false)
  const draftsSectionRef = useRef<HTMLDivElement>(null)

  // Refs for persisting on mouseUp without stale closures
  const sidebarWidthRef = useRef(sidebarWidth)
  sidebarWidthRef.current = sidebarWidth
  const draftsHeightRef = useRef(draftsHeight)
  draftsHeightRef.current = draftsHeight

  // Handle resize drag (horizontal)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      isDragging.current = true
      dragStartX.current = e.clientX
      dragStartWidth.current = sidebarWidth
      setIsResizing(true)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [sidebarWidth],
  )

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current) return

    const delta = e.clientX - dragStartX.current
    const newWidth = dragStartWidth.current + delta

    // Clamp to min/max, also limit to 50% of viewport width
    const maxViewportWidth = window.innerWidth * 0.5
    const clampedWidth = Math.min(MAX_WIDTH, maxViewportWidth, Math.max(MIN_WIDTH, newWidth))

    setSidebarWidth(clampedWidth)
  }, [])

  const handleMouseUp = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false
      setIsResizing(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      saveToLocalStorage('myrkup-sidebar-width', sidebarWidthRef.current)
    }
    if (isDraggingSection.current) {
      isDraggingSection.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      saveToLocalStorage('myrkup-drafts-height', draftsHeightRef.current)
    }
  }, [])

  // Section resize handlers (vertical)
  const handleSectionMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isDraggingSection.current = true
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }, [])

  const handleSectionMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingSection.current || !draftsSectionRef.current) return

    const sectionRect = draftsSectionRef.current.getBoundingClientRect()
    const newHeight = e.clientY - sectionRect.top

    const clampedHeight = Math.min(MAX_SECTION_HEIGHT, Math.max(MIN_SECTION_HEIGHT, newHeight))
    setDraftsHeight(clampedHeight)
  }, [])

  // Global mouse listeners
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mousemove', handleSectionMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mousemove', handleSectionMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp])

  return {
    sidebarWidth,
    draftsHeight,
    isResizing,
    handleMouseDown,
    handleSectionMouseDown,
    sidebarRef,
    draftsSectionRef,
  }
}
